import { Component, lazy, Suspense } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { MappableRestaurant } from '@/lib/types.ts';

// Heavy mapbox-gl consumer is isolated in MapboxMap.tsx and only enters the
// chunk graph when this lazy() boundary is crossed (i.e. when a map actually
// renders). Vite emits it as a separate dynamic-import chunk.
const MapboxMap = lazy(() => import('./MapboxMap.tsx'));

interface MapViewProps {
  restaurant?: MappableRestaurant & { name?: string; address?: string };
  height?: number;
  className?: string;
}

function parseCoord(val: string | number | null | undefined): number | null {
  if (val === null || val === undefined) return null;
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return typeof n === 'number' && !isNaN(n) ? n : null;
}

interface MapBoundaryProps {
  fallback: ReactNode;
  children: ReactNode;
}

/**
 * Error boundary around the lazy map chunk. Since the map is now a separate
 * dynamic import, a failed chunk fetch (transient CDN/network error, or an
 * older widget entry requesting a chunk hash a newer deploy has rotated) makes
 * `React.lazy` reject. `Suspense` only handles the loading state, so without
 * this boundary that rejection would unmount the whole booking flow over a
 * non-critical map. Here we degrade to the same placeholder and keep the flow
 * alive. (Telemetry for this is added in the observability ticket.)
 */
class MapErrorBoundary extends Component<MapBoundaryProps, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) return this.props.fallback;
    return this.props.children;
  }
}

/**
 * Interactive Mapbox GL JS map matching the member app's style.
 *
 * Wraps the heavy mapbox-gl consumer in a React.lazy boundary so mapbox-gl is
 * only fetched when the map step renders, keeping it out of the entry bundle.
 */
export default function MapView({ restaurant, height = 200, className }: MapViewProps) {
  const lat = parseCoord(restaurant?.latitude);
  const lon = parseCoord(restaurant?.longitude);

  if (lat === null || lon === null || !restaurant) return null;

  const placeholderStyle: CSSProperties = {
    width: '100%',
    height,
    borderRadius: 20,
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #e8e6e1 0%, #d8d5cf 100%)',
  };

  const placeholder = <div className={className} style={placeholderStyle} />;

  return (
    <MapErrorBoundary fallback={placeholder}>
      <Suspense fallback={placeholder}>
        <MapboxMap restaurant={restaurant} height={height} className={className} />
      </Suspense>
    </MapErrorBoundary>
  );
}
